import * as Sentry from '@sentry/nextjs';
import type {
  FetchQueryOptions,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';

interface FetchWithSentryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> extends FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey> {
  queryClient: QueryClient;
  tags: Record<string, string>;
  isRequired?: boolean;
}

/* This wraps React Query `fetchQuery` to report errors to Sentry.
 *
 * This should generally be used on the main page data fetch.
 *
 * The isRequired prop is important for statically defined pages like
 * /about or /terms where if the fetch returns null something has gone wrong
 * and we want to throw. Whereas dynamic (non-singleton) pages can return
 * null if we just happen to request a non-existent or un-published page.
 * It is worth noting that if isRequired is true, it is not worth adding
 * a `if(!data) { return { notFound: true } }` branch as it is unreachable. */
export async function fetchWithSentry<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  queryClient,
  tags,
  isRequired,
  ...options
}: FetchWithSentryOptions<
  TQueryFnData,
  TError,
  TData,
  TQueryKey
>): Promise<TData> {
  const reportToSentry = async (error: unknown) => {
    Sentry.withScope((scope) => {
      scope.setTags(tags);
      Sentry.captureException(error);
    });
    /* Build-time errors kill the Next.js worker right after this throws,
     * which can cut off the queued event before the SDK sends it. */
    await Sentry.flush(2000);
  };

  let data: TData;

  try {
    data = await queryClient.fetchQuery(options);
  } catch (error) {
    await reportToSentry(error);
    throw error;
  }

  if (isRequired && !data) {
    const error = new Error(
      `[fetchWithSentry] required query returned null (${String(options.queryKey[0])})`,
    );
    await reportToSentry(error);
    throw error;
  }

  return data;
}
